/**
 * Import Analytics Service
 * 
 * Tracks and analyzes import operations to provide insights into:
 * - Import success rates by source type
 * - Processing times and performance metrics
 * - Error patterns and failure analysis
 * - User satisfaction and feedback
 */


import { getFirebaseAuth } from '@watchlist/shared';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
  getDb,
  isFirebaseInitialized,
  unifiedAddDoc,
} from '@watchlist/shared';

// Types and Interfaces
export interface ImportError {
  code: string;
  message: string;
  details?: any;
  timestamp: Date;
}

export interface ImportAnalytics {
  sessionId: string;
  userId: string;
  source: 'screenshot' | 'text' | 'link' | 'recommendation';
  startTime: Date;
  endTime?: Date;
  moviesDetected: number;
  moviesSuccessful: number;
  moviesFiltered: number; // Adult content or duplicates filtered
  processingTime: number; // in milliseconds
  errors: ImportError[];
  userSatisfactionRating?: number; // 1-5 scale
  userFeedback?: string;
  inputSize?: number; // File size for images, character count for text
  platform?: string; // For link imports (imdb, letterboxd, etc.)
  deviceInfo: {
    isMobile: boolean;
    userAgent: string;
    screenSize: string;
  };
  performanceMetrics: {
    aiProcessingTime?: number;
    tmdbSearchTime?: number;
    totalNetworkTime?: number;
    memoryUsage?: number;
  };
}

export interface ImportStatistics {
  totalImports: number;
  successRate: number;
  averageProcessingTime: number;
  averageMoviesPerImport: number;
  errorFrequency: { [errorCode: string]: number };
  sourceTypeDistribution: { [source: string]: number };
  userSatisfactionAverage: number;
  commonFailurePatterns: string[];
}

export interface FeedbackSubmission {
  sessionId: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  appVersion?: string;
  rating: number;
  feedback: string;
  timestamp: Date;
  category: 'bug' | 'suggestion' | 'praise' | 'other';
  importSource: 'screenshot' | 'text' | 'link' | 'recommendation';
}

class ImportAnalyticsService {
  private static instance: ImportAnalyticsService;
  private currentSession: Partial<ImportAnalytics> | null = null;

  static getInstance(): ImportAnalyticsService {
    if (!ImportAnalyticsService.instance) {
      ImportAnalyticsService.instance = new ImportAnalyticsService();
    }
    return ImportAnalyticsService.instance;
  }

  /**
   * Start tracking a new import session
   */
  startImportSession(
    userId: string,
    source: 'screenshot' | 'text' | 'link' | 'recommendation',
    inputSize?: number,
    platform?: string
  ): string {
    const sessionId = this.generateSessionId();
    
    this.currentSession = {
      sessionId,
      userId,
      source,
      startTime: new Date(),
      moviesDetected: 0,
      moviesSuccessful: 0,
      moviesFiltered: 0,
      errors: [],
      inputSize,
      platform,
      deviceInfo: this.getDeviceInfo(),
      performanceMetrics: {}
    };

    console.log(`📊 Started import analytics session: ${sessionId}`);
    return sessionId;
  }

  /**
   * Update session with detected movies count
   */
  updateMoviesDetected(count: number): void {
    if (this.currentSession) {
      this.currentSession.moviesDetected = count;
    }
  }

  /**
   * Update session with successful movies count
   */
  updateMoviesSuccessful(count: number): void {
    if (this.currentSession) {
      this.currentSession.moviesSuccessful = count;
    }
  }

  /**
   * Update session with filtered movies count
   */
  updateMoviesFiltered(count: number): void {
    if (this.currentSession) {
      this.currentSession.moviesFiltered = count;
    }
  }

  /**
   * Add an error to the current session
   */
  recordError(error: ImportError): void {
    if (this.currentSession) {
      this.currentSession.errors!.push(error);
      console.warn(`📊 Import error recorded: ${error.code} - ${error.message}`);
    }
  }

  /**
   * Update performance metrics
   */
  updatePerformanceMetrics(metrics: Partial<ImportAnalytics['performanceMetrics']>): void {
    if (this.currentSession) {
      this.currentSession.performanceMetrics = {
        ...this.currentSession.performanceMetrics,
        ...metrics
      };
    }
  }

  /**
   * Complete the import session and save to Firebase
   */
  async completeImportSession(): Promise<void> {
    if (!this.currentSession || !this.currentSession.startTime) {
      console.warn('📊 No active import session to complete');
      return;
    }

    const endTime = new Date();
    const processingTime = endTime.getTime() - this.currentSession.startTime.getTime();

    const finalAnalytics: ImportAnalytics = {
      ...this.currentSession as ImportAnalytics,
      endTime,
      processingTime
    };

    try {
      await this.saveAnalytics(finalAnalytics);
      console.log(`📊 Import session completed and saved: ${finalAnalytics.sessionId}`);
      this.currentSession = null;
    } catch (error) {
      console.error('📊 Failed to save import analytics:', error);
    }
  }

  /**
   * Submit user feedback for an import session
   */
  async submitFeedback(
    sessionId: string,
    userId: string,
    rating: number,
    feedback: string,
    category: FeedbackSubmission['category'],
    importSource: 'screenshot' | 'text' | 'link' | 'recommendation',
    userInfo?: { userName?: string; userEmail?: string; appVersion?: string }
  ): Promise<void> {
    const feedbackData: FeedbackSubmission = {
      sessionId,
      userId,
      userName: userInfo?.userName,
      userEmail: userInfo?.userEmail,
      appVersion: userInfo?.appVersion,
      rating,
      feedback,
      timestamp: new Date(),
      category,
      importSource
    };

    try {
      await this.saveFeedback(feedbackData);
      
      // Also update the analytics record if it exists
      await this.updateSessionFeedback(sessionId, rating, feedback);
      
      console.log(`📊 User feedback submitted for session: ${sessionId}`);
    } catch (error) {
      console.error('📊 Failed to save user feedback:', error);
      throw error;
    }
  }

  /**
   * Get import statistics for analytics dashboard
   */
  async getImportStatistics(
    userId?: string,
    dateRange?: { start: Date; end: Date },
    source?: 'screenshot' | 'text' | 'link'
  ): Promise<ImportStatistics> {
    try {
      const analytics = await this.getAnalyticsData(userId, dateRange, source);
      return this.calculateStatistics(analytics);
    } catch (error) {
      console.error('📊 Failed to get import statistics:', error);
      throw error;
    }
  }

  /**
   * Get recent import sessions for debugging
   */
  async getRecentImports(userId?: string, limitCount: number = 50): Promise<ImportAnalytics[]> {
    try {
      return await this.getAnalyticsData(userId, undefined, undefined, limitCount);
    } catch (error) {
      console.error('📊 Failed to get recent imports:', error);
      throw error;
    }
  }

  /**
   * Get error patterns for debugging and improvement
   */
  async getErrorPatterns(
    dateRange?: { start: Date; end: Date },
    source?: 'screenshot' | 'text' | 'link'
  ): Promise<{ [errorCode: string]: { count: number; examples: ImportError[] } }> {
    try {
      const analytics = await this.getAnalyticsData(undefined, dateRange, source);
      const errorPatterns: { [errorCode: string]: { count: number; examples: ImportError[] } } = {};

      analytics.forEach(session => {
        session.errors?.forEach(error => {
          if (!errorPatterns[error.code]) {
            errorPatterns[error.code] = { count: 0, examples: [] };
          }
          errorPatterns[error.code].count++;
          if (errorPatterns[error.code].examples.length < 5) {
            errorPatterns[error.code].examples.push(error);
          }
        });
      });

      return errorPatterns;
    } catch (error) {
      console.error('📊 Failed to get error patterns:', error);
      throw error;
    }
  }

  // Private helper methods

  private generateSessionId(): string {
    return `import_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getDeviceInfo() {
    return {
      isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
      userAgent: navigator.userAgent,
      screenSize: `${window.screen.width}x${window.screen.height}`
    };
  }

  private async saveAnalytics(analytics: ImportAnalytics): Promise<void> {
    if (!getFirebaseAuth().currentUser) {
      throw new Error('User not authenticated');
    }

    // Convert dates to Firestore timestamps and filter out undefined values
    const analyticsData = {
      sessionId: analytics.sessionId,
      userId: analytics.userId,
      source: analytics.source,
      startTime: Timestamp.fromDate(analytics.startTime),
      endTime: analytics.endTime ? Timestamp.fromDate(analytics.endTime) : null,
      moviesDetected: analytics.moviesDetected,
      moviesSuccessful: analytics.moviesSuccessful,
      moviesFiltered: analytics.moviesFiltered,
      processingTime: analytics.processingTime,
      errors: analytics.errors.map(error => ({
        code: error.code,
        message: error.message,
        details: error.details || null,
        timestamp: Timestamp.fromDate(error.timestamp)
      })),
      deviceInfo: analytics.deviceInfo,
      performanceMetrics: analytics.performanceMetrics,
      // Only include optional fields if they have values
      ...(analytics.userSatisfactionRating !== undefined && { userSatisfactionRating: analytics.userSatisfactionRating }),
      ...(analytics.userFeedback !== undefined && { userFeedback: analytics.userFeedback }),
      ...(analytics.inputSize !== undefined && { inputSize: analytics.inputSize }),
      ...(analytics.platform !== undefined && { platform: analytics.platform })
    };

    await unifiedAddDoc('importAnalytics', analyticsData);
  }

  private async saveFeedback(feedback: FeedbackSubmission): Promise<void> {
    if (!getFirebaseAuth().currentUser) {
      throw new Error('User not authenticated');
    }

    const feedbackData = {
      ...feedback,
      timestamp: Timestamp.fromDate(feedback.timestamp)
    };

    await unifiedAddDoc('importFeedback', feedbackData);
  }

  private async updateSessionFeedback(
    sessionId: string,
    rating: number,
    feedback: string
  ): Promise<void> {
    // This would require a more complex update operation
    // For now, we'll store feedback separately and link by sessionId
    console.log(`📊 Feedback linked to session ${sessionId}: ${rating}/5`);
  }

  private async getAnalyticsData(
    userId?: string,
    dateRange?: { start: Date; end: Date },
    source?: 'screenshot' | 'text' | 'link',
    limitCount?: number
  ): Promise<ImportAnalytics[]> {
    const analyticsCollection = collection(getDb(), 'importAnalytics');

    let analyticsQuery = query(analyticsCollection);

    // Add filters
    if (userId) {
      analyticsQuery = query(analyticsQuery, where('userId', '==', userId));
    }

    if (source) {
      analyticsQuery = query(analyticsQuery, where('source', '==', source));
    }

    if (dateRange) {
      analyticsQuery = query(
        analyticsQuery,
        where('startTime', '>=', Timestamp.fromDate(dateRange.start)),
        where('startTime', '<=', Timestamp.fromDate(dateRange.end))
      );
    }

    // Add ordering and limit
    analyticsQuery = query(analyticsQuery, orderBy('startTime', 'desc'));
    
    if (limitCount) {
      analyticsQuery = query(analyticsQuery, limit(limitCount));
    }

    const querySnapshot = await getDocs(analyticsQuery);
    const analytics: ImportAnalytics[] = [];

    querySnapshot.forEach(doc => {
      const data = doc.data();
      analytics.push({
        ...data,
        startTime: data.startTime.toDate(),
        endTime: data.endTime?.toDate(),
        errors: data.errors?.map((error: any) => ({
          ...error,
          timestamp: error.timestamp.toDate()
        })) || []
      } as ImportAnalytics);
    });

    return analytics;
  }

  private calculateStatistics(analytics: ImportAnalytics[]): ImportStatistics {
    if (analytics.length === 0) {
      return {
        totalImports: 0,
        successRate: 0,
        averageProcessingTime: 0,
        averageMoviesPerImport: 0,
        errorFrequency: {},
        sourceTypeDistribution: {},
        userSatisfactionAverage: 0,
        commonFailurePatterns: []
      };
    }

    const totalImports = analytics.length;
    const successfulImports = analytics.filter(a => a.errors.length === 0).length;
    const successRate = (successfulImports / totalImports) * 100;

    const totalProcessingTime = analytics.reduce((sum, a) => sum + a.processingTime, 0);
    const averageProcessingTime = totalProcessingTime / totalImports;

    const totalMovies = analytics.reduce((sum, a) => sum + a.moviesDetected, 0);
    const averageMoviesPerImport = totalMovies / totalImports;

    const errorFrequency: { [errorCode: string]: number } = {};
    const sourceTypeDistribution: { [source: string]: number } = {};
    
    let totalRatings = 0;
    let ratingCount = 0;

    analytics.forEach(session => {
      // Count error frequencies
      session.errors?.forEach(error => {
        errorFrequency[error.code] = (errorFrequency[error.code] || 0) + 1;
      });

      // Count source type distribution
      sourceTypeDistribution[session.source] = (sourceTypeDistribution[session.source] || 0) + 1;

      // Calculate average satisfaction rating
      if (session.userSatisfactionRating) {
        totalRatings += session.userSatisfactionRating;
        ratingCount++;
      }
    });

    const userSatisfactionAverage = ratingCount > 0 ? totalRatings / ratingCount : 0;

    // Identify common failure patterns
    const commonFailurePatterns = Object.entries(errorFrequency)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([code]) => code);

    return {
      totalImports,
      successRate,
      averageProcessingTime,
      averageMoviesPerImport,
      errorFrequency,
      sourceTypeDistribution,
      userSatisfactionAverage,
      commonFailurePatterns
    };
  }
}

export const importAnalyticsService = ImportAnalyticsService.getInstance(); 